/* review-blocks.js — RW-01a html-backed block model for the review workspace.
   Replaces the prototype's SEED/gen() model. Derived bands are a display overlay
   only, never authority (D7); the transient display document is never persisted (D9). */
import {
  indexHtmlSource, nodeText, deriveSections, injectSections, buildSectionMap, secLabel,
  buildElementIndex, SECTION_LABELS } from './source-map.js';
import { sanitizeForPreview, INSPECTOR_CSS, INSPECTOR_JS } from './preview-doc.js';
import { deriveBands, bandsIdentity } from '../../../shared/review-bands.js';

import { applyFieldEdits } from '../../../shared/review-fields.js';
import { projectManualFields } from '../../../shared/editor-fields.js';
export { applyFieldEdits };

// Voluntary same-context SPA navigation only. Logout, access revocation and
// workspace changes must still clear their old context without a veto.
export function requestNavigation(target, context) {
  return target.dispatchEvent(new target.CustomEvent('goose-before-navigation', { cancelable: true, detail: { context } }));
}

// A preview is ONE server proposal, never the union of unrelated alternatives.
// It is advisory and refuses to apply atop a changed local/base file.
export function previewChaseProposal(html, index, queue, proposalId) {
  const patches = (queue || []).filter(p => p.pid === proposalId);
  if (!patches.length || patches.some(p => p.stale)) return { html, reason: 'stale_or_missing' };
  if (patches.some(p => p.blockId === 'document')) {
    if (patches.length !== 1 || patches[0].before !== html) return { html, reason: 'base_changed' };
    return { html: patches[0].after, reason: null };
  }
  if (patches.some(p => bandCode(html, index, p.blockId) !== p.before)) return { html, reason: 'base_changed' };
  return { html: spliceBands(html, index, Object.fromEntries(patches.map(p => [p.blockId, p.after]))), reason: null };
}
export function previewChaseProposalSet(html, index, queue, set) {
  if (!set?.enabled || !Array.isArray(set.proposalIds) || set.proposalIds.length < 2
      || new Set(set.proposalIds).size !== set.proposalIds.length) return { html, reason: 'set_unavailable' };
  const patches = (queue || []).filter(p => set.proposalIds.includes(p.pid));
  if (!patches.length || patches.some(p => p.stale) || set.proposalIds.some(id => !patches.some(p => p.pid === id))) return { html, reason: 'stale_or_missing' };
  if (patches.some(p => p.blockId === 'document')) return { html, reason: 'document_scope_conflict' };
  if (new Set(patches.map(p => p.blockId)).size !== patches.length) return { html, reason: 'overlapping_proposals' };
  if (patches.length !== set.patches?.length || patches.length !== set.regionIds?.length
      || patches.some(p => !set.regionIds.includes(p.blockId) || !set.patches.some(expected => expected.region_id === p.blockId && expected.after_code === p.after)))
    return { html, reason: 'set_members_changed' };
  const current = findBands(html).index;
  const ranges = [];
  for (const patch of patches) {
    const matches = current?.bands?.filter(b => b.sec === patch.blockId) || [];
    if (matches.length !== 1) return { html, reason: 'base_changed' };
    const band = matches[0];
    if (!band?.authored || !band.range || bandCode(html, current, patch.blockId) !== patch.before
        || bandCode(html, index, patch.blockId) !== patch.before) return { html, reason: 'base_changed' };
    ranges.push(band.range);
  }
  ranges.sort((a, b) => a.start - b.start);
  if (ranges.some((range, i) => i && range.start < ranges[i - 1].end)) return { html, reason: 'overlapping_ranges' };
  return { html: spliceBands(html, current, Object.fromEntries(patches.map(p => [p.blockId, p.after]))), reason: null };
}
export const RW_CSP = "default-src 'none'; img-src 'self' data:; style-src 'unsafe-inline'; font-src 'self'";
export const RW_SELECT_CSS = `[data-region],[data-sec]{cursor:pointer;transition:outline-color .12s,background-color .12s}
[data-region]:hover,[data-sec]:hover{outline:2px dashed rgba(46,115,184,.5);outline-offset:-2px}
.gx-sel{outline:2px solid #2E73B8 !important;outline-offset:-2px;background:rgba(46,115,184,.06)}
.gx-patch{outline:2px dashed #4F9D69 !important;outline-offset:-2px;background:rgba(79,157,105,.09)}`;

const QA_TAGS = new Set(['pass', 'minor', 'major']);
const QA_SEVERITY = { pass: 1, minor: 2, major: 3 };
const FINDING_STOP_WORDS = new Set(['and', 'the', 'copy', 'images', 'buttons', 'code']);

export function parseQaFindings(md) {
  const lines = String(md ?? '').split('\n');
  const marker = lines.findIndex((line) => /^\s*Section-by-section findings:\s*$/i.test(line));
  if (marker < 0) return [];

  const findings = [];
  for (let i = marker + 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) {
      if (findings.length) break;
      continue;
    }
    const match = /^\s*[-*]\s+(.+?):\s*(pass|minor|major)\b\s*(?:[—–-]\s*)?(.*)\s*$/i.exec(line);
    if (!match) {
      if (findings.length || /^\s*#{1,6}\s/.test(line)) break;
      continue;
    }
    findings.push({
      label: match[1].replace(/^(?:\*\*|__)|(?:\*\*|__)$/g, '').trim(),
      tag: match[2].toLowerCase(),
      note: match[3].trim(),
    });
  }
  return findings;
}

function findingTokens(value) {
  return String(value ?? '')
    .toLowerCase()
    .split(/[\/\s_+()-]+/)
    .map((word) => word.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, ''))
    .filter((word) => word && !FINDING_STOP_WORDS.has(word))
    // The QA category "Disclaimer" names the same content family as footer/legal
    // copy; the supplied seed contains only "Footer" (D7 display-only join metadata).
    .map((word) => /^(?:footer|legal|disclaimer|unsubscribe)$/.test(word) ? 'footer-legal' : word);
}

export function joinFindings(bands, findings, html) {
  const bandList = Array.isArray(bands) ? bands : [];
  const findingList = Array.isArray(findings) ? findings : [];
  const text = String(html ?? '');
  const idx = indexHtmlSource(text);
  const haystacks = bandList.map((band) => {
    const nd = idx.nodes.find((node) => node.kind === 'el' && node.openStart === band?.range?.start);
    const visible = nd ? nodeText(idx, nd.id) : '';
    return new Set(findingTokens(`${band?.label ?? ''} ${visible}`));
  });

  const bestByBand = new Map();
  const matched = new Set();
  findingList.forEach((finding, findingIndex) => {
    const tokens = [...new Set(findingTokens(finding?.label ?? finding?.section))];
    let bestBand = -1;
    let bestScore = 0;
    haystacks.forEach((words, bandIndex) => {
      const score = tokens.reduce((sum, token) => sum + (words.has(token) ? 1 : 0), 0);
      if (score > bestScore) {
        bestScore = score;
        bestBand = bandIndex;
      }
    });
    if (bestBand < 0 || bestScore < 1) return;

    matched.add(findingIndex);
    const tag = String(finding?.tag ?? '').toLowerCase();
    const candidate = { finding, findingIndex, score: bestScore, tag };
    const current = bestByBand.get(bestBand);
    if (!current
      || candidate.score > current.score
      || (candidate.score === current.score
        && (QA_SEVERITY[candidate.tag] || 0) > (QA_SEVERITY[current.tag] || 0))) {
      bestByBand.set(bestBand, candidate);
    }
  });

  const byBand = {};
  for (const [bandIndex, candidate] of bestByBand) {
    const band = bandList[bandIndex];
    Object.defineProperty(byBand, band.sec, {
      configurable: true,
      enumerable: true,
      writable: true,
      value: {
        qa: QA_TAGS.has(candidate.tag) ? candidate.tag : candidate.finding.tag,
        note: candidate.finding.note ?? '',
      },
    });
  }
  return {
    byBand,
    unplaced: findingList.filter((_, index) => !matched.has(index)),
  };
}

export function findBands(html) {
  let text;
  try {
    text = String(html ?? '');
    const rawIndex = buildElementIndex(text);
    const sharedBands = deriveBands(text);
    const bands = sharedBands.map((band) => {
      const startPos = rawIndex.idx.posOf(band.start);
      const endPos = rawIndex.idx.posOf(band.end);
      const curated = SECTION_LABELS[band.sec];   // RW-05 (🐓): identity/ranges are the shared deriver's (ruled); LABELS stay FE presentation — curated ids get their human label, everything else keeps the deriver's
      return {
        sec: band.sec,
        label: curated || band.label,
        elKey: band.sec,
        authored: band.authored,
        identity: band.authored ? 'authored' : 'derived',
        anchor_attr: band.anchor_attr,
        order: band.order,
        range: {
          start: band.start,
          end: band.end,
          startLine: startPos.line,
          startCh: startPos.ch,
          endLine: endPos.line,
          endCh: endPos.ch,
        },
      };
    });
    const ok = bands.length > 0;
    const index = { ...rawIndex, ok, reason: ok ? null : 'no-shell', bands };
    return {
      ok,
      reason: index.reason,
      identity: bandsIdentity(sharedBands),
      bands,
      index,
    };
  } catch (_) {
    const idx = indexHtmlSource(text || '');
    const index = { ok: false, reason: 'no-shell', bands: [], elements: [], idx };
    return { ok: false, reason: 'no-shell', identity: bandsIdentity([]), bands: [], index };
  }
}

export function blocksFromHtml(html, { findings = [], mockFindings = null } = {}) {
  const text = String(html ?? '');
  const found = findBands(text);
  const supplied = mockFindings == null ? findings : mockFindings;
  if (!found.ok) {
    return {
      ok: false,
      reason: found.reason,
      identity: found.identity,
      blocks: [],
      index: found.index,
      unplaced: Array.isArray(supplied) ? supplied : [],
    };
  }

  const joined = joinFindings(found.bands, supplied, text);
  const blocks = found.bands.map((band) => {
    const code = text.slice(band.range.start, band.range.end);
    const qa = joined.byBand[band.sec]?.qa ?? null;
    const note = joined.byBand[band.sec]?.note ?? null;
    return {
      id: band.sec,
      label: band.label,
      code,
      qa,
      note,
      p: {},
      custom: false,
      ico: iconFor(band.label, code),
      identity: band.identity,
      anchor_attr: band.anchor_attr,
      elKey: band.elKey,
      range: band.range,
      review_state: qa === 'major' ? 'blocked' : qa === 'minor' ? 'attention' : 'clean',
    };
  });
  return {
    ok: true,
    reason: found.reason,
    identity: found.identity,
    blocks,
    index: found.index,
    unplaced: joined.unplaced,
  };
}

export function bandCode(html, index, id) {
  const band = index?.bands?.find((entry) => entry.sec === id);
  if (!band) return null;
  return String(html ?? '').slice(band.range.start, band.range.end);
}

// Carry the server's ranges through local byte edits without deriving new ids.
// Whole-file edits that overwrite a range boundary cannot safely reseat that
// block; it stays listed, with no splice range, until Apply/reload supplies one.
export function rebaseBlocks(blocks, before, after, edits = null) {
  if (!edits) {
    let start = 0, end = before.length, nextEnd = after.length;
    while (start < end && start < nextEnd && before[start] === after[start]) start++;
    while (end > start && nextEnd > start && before[end - 1] === after[nextEnd - 1]) {end--;nextEnd--;}
    edits = start === end && start === nextEnd ? [] : [{ start, end, text: after.slice(start, nextEnd) }];
  }
  const ordered = [...edits].sort((a, b) => b.start - a.start);
  return blocks.map((block) => {
    let range = block.range && { ...block.range };
    for (const edit of ordered) {
      if (!range) break;
      const delta = edit.text.length - (edit.end - edit.start);
      if (edit.blockId === block.id) range.end = range.start + edit.text.length;
      else if (edit.end <= range.start) {range.start += delta;range.end += delta;}
      else if (edit.start >= range.end) continue;
      else if (edit.start >= range.start && edit.end <= range.end) range.end += delta;
      else range = null;
    }
    if (range) {
      const position = (offset) => {const lines = after.slice(0, offset).split('\n');return { line: lines.length - 1, ch: lines[lines.length - 1].length };};
      const start = position(range.start), end = position(range.end);
      range = { ...range, startLine: start.line, startCh: start.ch, endLine: end.line, endCh: end.ch };
    }
    const code = range ? after.slice(range.start, range.end) : '';
    const hadBoundFields = Object.values(block.fields || {}).some(f => f?.schema === 'ArtifactField.v1');
    const invalidated = (hadBoundFields || block.fieldProjection === 'artifact') && code !== block.code;
    const fields = invalidated && range ? projectManualFields(code, { source: 'local_draft_html' }) : {};
    return { ...block, range, code, ...(invalidated ? { fields, fieldProjection: 'artifact', p: Object.fromEntries(Object.entries(fields).map(([key, f]) => [key, f.value])), custom: true,
      manual_unavailable: Object.keys(fields).length ? null : 'No safe Manual fields can be projected from this draft. Use Code; server replay and full-email QA still govern Apply.' } : {}) };
  });
}

export function spliceBand(html, index, id, code) {
  const text = String(html ?? '');
  const band = index?.bands?.find((entry) => entry.sec === id);
  if (!band || !band.range) return text;
  return text.slice(0, band.range.start) + String(code ?? '') + text.slice(band.range.end);
}

export function spliceBands(html, index, map) {
  let out = String(html ?? '');
  if (!map || typeof map !== 'object') return out;
  const bands = (index?.bands || [])
    .filter((band) => band.range && Object.prototype.hasOwnProperty.call(map, band.sec))
    .sort((a, b) => b.range.start - a.range.start);
  for (const band of bands) {
    out = out.slice(0, band.range.start)
      + String(map[band.sec] ?? '')
      + out.slice(band.range.end);
  }
  return out;
}

export function diffBands(before, after) {
  const beforeText = String(before ?? '');
  const afterText = String(after ?? '');
  const beforeFound = findBands(beforeText);
  const afterFound = findBands(afterText);
  const beforeCodes = new Map(beforeFound.bands.map((band) => [band.sec, bandCode(beforeText, beforeFound.index, band.sec)]));
  const afterCodes = new Map(afterFound.bands.map((band) => [band.sec, bandCode(afterText, afterFound.index, band.sec)]));
  const ids = [...beforeCodes.keys(), ...[...afterCodes.keys()].filter((id) => !beforeCodes.has(id))];
  return ids.filter((id) => beforeCodes.get(id) !== afterCodes.get(id));
}

export function applyPatches(html, patches) {
  const text = String(html ?? '');
  const found = findBands(text);
  const map = Object.create(null);
  for (const patch of Array.isArray(patches) ? patches : []) {
    if (patch && patch.blockId != null) map[patch.blockId] = patch.code;
  }
  return spliceBands(text, found.index, map);
}

function isAbsoluteAsset(ref) {
  return /^(?:[a-z][a-z0-9+.-]*:|\/\/|#|[\\/])/i.test(ref);
}

function isDynamicAsset(ref) {
  return /(?:\{\{|\{%|<%|%%|\$\{|\*\|)/.test(ref);
}

function openTagAttrs(tag) {
  const attrs = [];
  let i = 1;
  while (i < tag.length && /\s/.test(tag[i])) i++;
  while (i < tag.length && !/[\s/>]/.test(tag[i])) i++; // element name
  while (i < tag.length) {
    while (i < tag.length && /\s/.test(tag[i])) i++;
    if (i >= tag.length || tag[i] === '>' || (tag[i] === '/' && tag[i + 1] === '>')) break;
    const nameStart = i;
    while (i < tag.length && !/[\s=/>]/.test(tag[i])) i++;
    if (i === nameStart) { i++; continue; }
    const name = tag.slice(nameStart, i).toLowerCase();
    while (i < tag.length && /\s/.test(tag[i])) i++;
    if (tag[i] !== '=') continue;
    i++;
    while (i < tag.length && /\s/.test(tag[i])) i++;
    const quote = tag[i] === '"' || tag[i] === "'" ? tag[i++] : '';
    const start = i;
    if (quote) {
      while (i < tag.length && tag[i] !== quote) i++;
    } else {
      while (i < tag.length && !/[\s>]/.test(tag[i])) i++;
    }
    attrs.push({ name, start, end: i });
    if (quote && tag[i] === quote) i++;
  }
  return attrs;
}

export function rewritePreviewAssets(html, { artifactUrl, prefix = '03_execution/' } = {}) {
  const text = String(html ?? '');
  const unresolved = [];
  let rewritten = 0;

  const rewriteRef = (raw) => {
    const ref = String(raw ?? '').trim();
    if (isAbsoluteAsset(ref)) return raw;
    if (!ref || isDynamicAsset(ref) || typeof artifactUrl !== 'function') {
      unresolved.push(raw);
      return raw;
    }
    try {
      const next = artifactUrl(String(prefix ?? '') + ref);
      if (typeof next !== 'string' || !next) {
        unresolved.push(raw);
        return raw;
      }
      rewritten++;
      return next;
    } catch (_) {
      unresolved.push(raw);
      return raw;
    }
  };

  const idx = indexHtmlSource(text);
  const replacements = [];
  for (const nd of idx.nodes) {
    if (nd.kind !== 'el') continue;
    const original = text.slice(nd.openStart, nd.openEnd);
    const attrReplacements = [];
    for (const attr of openTagAttrs(original)) {
      const value = original.slice(attr.start, attr.end);
      let next = value;
      if (attr.name === 'src' || attr.name === 'background') {
        next = rewriteRef(value);
      } else if (attr.name === 'style') {
        next = value.replace(
          /url\(\s*(?:(["'])(.*?)\1|([^)]*?))\s*\)/gi,
          (url, innerQuote, quotedRef, bareRef) => {
            const rawRef = innerQuote ? quotedRef : bareRef;
            const entityQuote = !innerQuote
              && /^(&(?:quot|apos);|&#(?:34|39);|&#x(?:22|27);)([\s\S]*)\1$/i.exec(rawRef);
            const ref = entityQuote ? entityQuote[2] : rawRef;
            const rewrittenRef = rewriteRef(ref);
            return rewrittenRef === ref
              ? url
              : `url(${innerQuote || entityQuote?.[1] || ''}${rewrittenRef}${innerQuote || entityQuote?.[1] || ''})`;
          },
        );
      }
      if (next !== value) attrReplacements.push({ ...attr, value: next });
    }
    let tag = original;
    for (let i = attrReplacements.length - 1; i >= 0; i--) {
      const replacement = attrReplacements[i];
      tag = tag.slice(0, replacement.start) + replacement.value + tag.slice(replacement.end);
    }
    if (tag !== original) replacements.push({ start: nd.openStart, end: nd.openEnd, value: tag });
  }

  let out = text;
  for (let i = replacements.length - 1; i >= 0; i--) {
    const replacement = replacements[i];
    out = out.slice(0, replacement.start) + replacement.value + out.slice(replacement.end);
  }
  return { html: out, rewritten, unresolved };
}

function addDisplayHead(doc, csp, selectCss) {
  const extras = `<meta http-equiv="Content-Security-Policy" content="${csp}"><style id="rw-select">${selectCss}</style>`;
  const idx = indexHtmlSource(doc);
  const head = idx.nodes.find((node) => node.kind === 'el' && node.tag === 'head');
  if (head) return doc.slice(0, head.openEnd) + extras + doc.slice(head.openEnd);
  const html = idx.nodes.find((node) => node.kind === 'el' && node.tag === 'html');
  if (html) return doc.slice(0, html.openEnd) + `<head>${extras}</head>` + doc.slice(html.openEnd);
  const doctype = idx.nodes.find((node) => node.kind === 'decl'
    && /^<!doctype\b/i.test(doc.slice(node.start, node.end)));
  if (doctype) {
    const at = doctype.end;
    return doc.slice(0, at) + `<head>${extras}</head>` + doc.slice(at);
  }
  return `<head>${extras}</head>` + doc;
}

function injectDisplayBands(doc, bands) {
  let out = doc;
  const derived = (Array.isArray(bands) ? bands : [])
    .filter((band) => !band.authored)
    .sort((a, b) => b.range.start - a.range.start);
  for (const band of derived) {
    const at = band.range.start + 3;
    out = out.slice(0, at) + ` data-sec="${band.sec}"` + out.slice(at);
  }
  return out;
}

export function buildDisplayDoc(html, {
  artifactUrl = null,
  csp = RW_CSP,
  selectCss = RW_SELECT_CSS,
  bridge = null,
} = {}) {
  const source = String(html ?? '');
  const found = findBands(source);
  let doc = sanitizeForPreview(source);
  // Sanitization preserves offsets, so stamp before asset rewriting changes string
  // lengths. Authored siblings pass through, and nested anchors cannot suppress rows.
  doc = injectDisplayBands(doc, found.bands);
  let rewritten = 0;
  let unresolved = [];
  if (artifactUrl) {
    const assets = rewritePreviewAssets(doc, { artifactUrl });
    doc = assets.html;
    rewritten = assets.rewritten;
    unresolved = assets.unresolved;
  }
  // RWB-12a bridge posture (🌑 pre-read 2026-09-07 cond. 4): no 'self' on an opaque origin; base-uri/form-action closed as belt and braces.
  if (bridge) csp = `default-src 'none'; img-src ${bridge.assetsOrigin} data:; style-src 'unsafe-inline'; font-src ${bridge.assetsOrigin}; script-src 'nonce-${bridge.nonce}'; base-uri 'none'; form-action 'none'`;
  doc = addDisplayHead(doc, csp, selectCss);
  if (bridge) {
    const payload = `<style id="goose-inspector-css">${INSPECTOR_CSS}</style>\n<script nonce="${bridge.nonce}" id="goose-inspector-js">${INSPECTOR_JS}</scr` + `ipt>`;
    if (/<\/body\s*>/i.test(doc)) doc = doc.replace(/<\/body\s*>/i, () => payload + '\n</body>');
    else doc += '\n' + payload;
  }
  return {
    doc,
    ...(bridge ? { nonce: bridge.nonce } : {}),
    identity: found.identity,
    bands: found.bands,
    rewritten,
    unresolved,
  };
}

export function iconFor(label, code) {
  const source = String(code ?? '');
  const visible = `${label ?? ''} ${source.replace(/<[^>]*>/g, ' ').replace(/&[^;]+;/g, ' ')}`
    .replace(/\s+/g, ' ')
    .trim();
  const category = `${label ?? ''} ${source}`;
  if (/<img\b/i.test(source)) return 'image';
  if (/<a\s/i.test(source) && visible.length <= 80) return 'cursor';
  if (/\b(?:accent\s+bar|spacer|rule|divider)\b/i.test(category) || /<hr\b/i.test(source)) return 'minus';
  if (/\b(?:footer|legal|disclaimer)\b/i.test(category)) return 'doc';
  if (/\b(?:headline|copy|body)\b/i.test(category)) return 'type';
  if (/<table\b/i.test(source) || /\b(?:table|grid)\b/i.test(category)) return 'grid';
  return 'layers';
}

if (typeof window !== 'undefined') window.GooseReviewBlocks = {
  requestNavigation,
  RW_CSP,
  RW_SELECT_CSS,
  parseQaFindings,
  joinFindings,
  findBands,
  blocksFromHtml,
  rebaseBlocks,
  bandCode,
  spliceBand,
  spliceBands,
  diffBands,
  applyPatches,
  applyFieldEdits,
  previewChaseProposal,
  previewChaseProposalSet,
  rewritePreviewAssets,
  buildDisplayDoc,
  iconFor,
  buildElementIndex,
  deriveSections,
  secLabel,
  injectSections,
  buildSectionMap,
  sanitizeForPreview,
};
